import type { ChartConfig } from "@/shared/ui/ChartContainer";
import { RadialChart, type RadialChartData } from "@/shared/ui/RadialChart";
import { Card, CardContent, CardHeader, CardTitle } from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import type { ClassSessionDTO } from "../../domain/contracts/sessions";

type ClassSessionAttendanceSummaryProps = {
  session: ClassSessionDTO;
};

export function ClassSessionAttendanceSummary({
  session,
}: ClassSessionAttendanceSummaryProps) {
  const enrolled = session.attendanceCount ?? 0;
  const capacity = session.capacity;
  const filledPercentage =
    capacity > 0 ? Math.round((enrolled / capacity) * 100) : 0;
  const waitlist = 0; // TODO: Get from actual data when available
  const checkIns = enrolled; // Using attendanceCount as check-ins for now

  const chartData: RadialChartData[] = [
    {
      name: "filled",
      value: filledPercentage,
    },
  ];

  const chartConfig: ChartConfig = {
    filled: {
      label: "ເຕັມຄວາມຈຸ",
      color: "hsl(var(--primary))",
    },
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>ສະຫຼຸບການເຂົ້າຮ່ວມ</CardTitle>
          <Link
            to="/app/attendance"
            search={{ sessionId: session.id }}
            className="text-primary text-sm hover:underline"
          >
            ເບິ່ງລາຍຊື່ຜູ້ເຂົ້າຮ່ວມ
          </Link>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Donut Chart */}
        <div className="flex justify-center">
          <RadialChart
            data={chartData}
            config={chartConfig}
            centerValue={`${filledPercentage}%`}
            centerLabel="ເຕັມຄວາມຈຸ"
            className="mx-auto aspect-square max-h-[200px]"
            startAngle={90}
            endAngle={450}
            innerRadius={60}
            outerRadius={85}
          />
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <div>
            <p className="text-muted-foreground text-xs uppercase tracking-wide">
              ລົງທະບຽນແລ້ວ
            </p>
            <p className="mt-1 font-semibold text-lg">{enrolled}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs uppercase tracking-wide">
              ຄວາມຈຸ
            </p>
            <p className="mt-1 font-semibold text-lg">{capacity}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs uppercase tracking-wide">
              ລາຍລໍຖ້າ
            </p>
            <p className="mt-1 font-semibold text-lg">{waitlist}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs uppercase tracking-wide">
              ເຂົ້າຮຽນແລ້ວ
            </p>
            <p className="mt-1 font-semibold text-lg text-primary">
              {checkIns}
            </p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${filledPercentage}%` }}
            />
          </div>
          <p className="text-center text-muted-foreground text-xs">
            {filledPercentage}% ເຕັມຄວາມຈຸ
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
